import type { ReactNode } from 'react'

interface TooltipEntry {
    name?: string | number
    value?: number | string | Array<number | string>
    color?: string
    fill?: string
    stroke?: string
    dataKey?: string | number | ((value: unknown) => unknown)
    payload?: Record<string, unknown>
}

export interface ChartTooltipProps {
    active?: boolean
    payload?: TooltipEntry[]
    label?: string | number
    valueFormatter?: (value: number) => string
    labelKey?: string
}

// Tooltip card shared by every chart: label on top, one row per series.
const ChartTooltip = ({ active, payload, label, valueFormatter, labelKey }: ChartTooltipProps): ReactNode => {
    if (!active || !payload || payload.length === 0) return null
    const title = labelKey ? String(payload[0]?.payload?.[labelKey] ?? label ?? '') : String(label ?? '')

    return (
        <div className='min-w-36 rounded-lg border bg-popover px-3 py-2 text-xs shadow-elevated'>
            {title && <p className='mb-1.5 font-medium text-foreground'>{title}</p>}
            <div className='grid gap-1'>
                {payload.map((entry, index) => {
                    const numeric = typeof entry.value === 'number' ? entry.value : Number(entry.value)
                    return (
                        <div key={`${String(entry.name)}-${index}`} className='flex items-center justify-between gap-4'>
                            <span className='flex items-center gap-1.5 text-muted-foreground'>
                                <span className='size-2 rounded-[3px]' style={{ background: entry.color || entry.fill || entry.stroke }} aria-hidden='true' />
                                {entry.name}
                            </span>
                            <span className='font-semibold text-foreground tabular-nums'>
                                {valueFormatter && Number.isFinite(numeric) ? valueFormatter(numeric) : String(entry.value ?? '')}
                            </span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default ChartTooltip
